import { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { logout, setProfileDialogueShowing, siteboardAction } from '../actions/defaultActions';
import { SET_NAME, SET_SUBSCRIPTION_CHOOSER_DIALOGUE_OPEN, isSubscriptionStarted } from '../actions/types';

import PopupWindow from './PopupWindow';
import CollapsibleContainer from './CollapsibleContainer';
import SubscriptionCard from './SubscriptionCard';
import PushButton from './PushButton';

import BackIcon from '../icons/backIcon';
import ExitIcon from '../icons/exitIcon';

const MyProfileDialogue = ({ _users, _userId, _subscriptions, logout, setProfileDialogueShowing, siteboardAction }) => {
  const getUserEmail = () => { try { return _users[_userId].email; } catch(error) { return ''; } };
  const getUserFirstName = useCallback(() => { try { return _users[_userId].firstName; } catch(error) { return ''; } }, [_users, _userId]);
  const getUserLastName = useCallback(() => { try { return _users[_userId].lastName; } catch(error) { return ''; } }, [_users, _userId]);

  const [firstName, setFirstName] = useState(getUserFirstName());
  const [lastName, setLastName] = useState(getUserLastName());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  let ownerOfSubscription = null;
  const otherSubscriptions = [];
  for(const subscriptionId in _subscriptions) {
    const subscription = _subscriptions[subscriptionId];
    if(!(_userId in subscription.members)) continue;
    if(!isSubscriptionStarted(subscription)) continue;

    if(subscription.members[_userId].role === 'Owner') {
      ownerOfSubscription = subscription;
    } else if(subscription.members[_userId].role === 'Admin' || subscription.members[_userId].role === 'Member') {
      otherSubscriptions.push(subscription);
    }
  }

  useEffect(() => {
    setHasUnsavedChanges(firstName !== getUserFirstName() || lastName !== getUserLastName());
  }, [firstName, lastName, getUserFirstName, getUserLastName]);

  const closeDialogue = () => {
    setProfileDialogueShowing(false);
  };

  return (
    <PopupWindow title='Account' closeDialogue={closeDialogue} bgStyle='bg-[#333333]'>
      <div className='flex flex-col gap-y-6 max-w-screen-lg text-white'>
        <div className='flex flex-col gap-y-2 w-[90vw] max-w-full'>
          <div className='flex flex-col gap-2'>
            <div className=''>
              <label className='block text-sm font-medium mb-1'>Email</label>
              <input
                className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] cursor-not-allowed' 
                type="text"
                disabled={true}
                value={getUserEmail()}
              />
            </div>
            {/* Logout button */}
            <PushButton 
              textColor='white'
              bgColor='#471cff'
              hoverBgColor='#603aff'
              onClick={() => {
                closeDialogue();
                logout();
              }}
            >
              <div className='flex items-center gap-x-[6px]'>
                <ExitIcon size='1.4em' /> 
                <div>Log Out</div>
              </div>
            </PushButton>
          </div>
          <div className='w-full h-[1px] bg-[#555] my-3'></div>
          <div className='flex flex-col md:flex-row md:items-end gap-2'>
            <div className='md:w-1/2 '>
              <label className='block text-sm font-medium mb-1'>First Name</label>
              <input
                className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
                type="text"
                value={firstName}
                onChange={(event) => { setFirstName(event.target.value); }}
              />
            </div>
            <div className='md:w-1/2 '>
              <label className='block text-sm font-medium mb-1'>Last Name</label>
              <input
                className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
                type="text"
                value={lastName}
                onChange={(event) => { setLastName(event.target.value); }}
              />
            </div>
          </div>
        </div>
        {/* Save name button */}
        <div>
          <CollapsibleContainer isOpen={hasUnsavedChanges}>
            <div className='flex gap-x-2 mb-6'>
              <button  
                className={`border border-[#888] hover:border-[#000] hover:bg-[#000] text-white w-full py-[7px] font-bold select-none rounded-lg`}
                disabled={!hasUnsavedChanges}
                onClick={() => {
                  setFirstName(getUserFirstName());
                  setLastName(getUserLastName());
                }}
              >
                Cancel
              </button>
              <PushButton 
                textColor='white'
                bgColor='#471cff'
                hoverBgColor='#603aff'
                disabled={!hasUnsavedChanges}
                onClick={() => {
                  const onSuccess = (output) => { 
                    setFirstName(output.user.firstName);
                    setLastName(output.user.lastName);
                  };
                  siteboardAction(SET_NAME, {
                    firstName: firstName,
                    lastName: lastName,
                  }, onSuccess);
                }}
              >
                Save Name
              </PushButton>
            </div>
          </CollapsibleContainer>
          <div className='w-full h-[1px] bg-[#555]'></div>
        </div>

        {/* My Subscription */}
        {ownerOfSubscription !== null &&
          <div className='flex flex-col gap-y-4'>
            <div className='font-bold text-[1.4em] leading-[1.2em]'>Your Subscription</div>
            <SubscriptionCard subscription={ownerOfSubscription} />
          </div>
        }

        {(ownerOfSubscription !== null && otherSubscriptions.length > 0) && 
          <div className='w-full h-[1px] bg-[#555]'></div>
        }

        {/* Other Subscriptions */}
        {otherSubscriptions.length > 0 && 
          <div className='font-bold text-[1.4em] leading-[1.2em]'>Accounts You've Joined</div>
        }
        {otherSubscriptions.map((otherSubscription, index) => (
          <SubscriptionCard key={index} subscription={otherSubscription} />
        ))}

        {ownerOfSubscription === null &&
        <>
          <div className='w-full h-[1px] bg-[#555]'></div>
          <div className='font-bold text-[1.4em] leading-[1.2em]'>Your Subscription</div>
          <div className='flex flex-col gap-3'>
            <div>You don’t currently have your own subscription. </div>
            <div>If you'd like to manage projects separately from the accounts you've joined you can create your own.</div>
            <PushButton 
              textColor='black'
              bgColor='#f9a928'
              hoverBgColor='#f79800'
              onClick={() => { siteboardAction(SET_SUBSCRIPTION_CHOOSER_DIALOGUE_OPEN, { isOpen: true }); }}
            >
              Sign Up
            </PushButton>
          </div>
        </>
        }
      </div>
    </PopupWindow>
  );
};


const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { logout, setProfileDialogueShowing, siteboardAction })(MyProfileDialogue);
